import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useSignalR } from '@s20/hooks/signalr.hooks';
import { httpClient } from '@s20/services/httpClient';
import { getTenantId } from '@s20/selectors/features/common.selectors';
import { ApiRoutes } from '../../../Common';

/**
 * Crew lead vehicle's route + ETA to the job site for the given timesheet.
 *
 * - Initial snapshot via REST when the job is opened (cached audit position).
 * - Live updates via SignalR: while subscribed, the server polls the crew lead truck on a cadence
 *   and pushes fresh position + ETA. Each push updates the react-query caches (route/ETA, and the
 *   truck's marker in the equipment layer) — reactive, no client polling. The server only polls
 *   while a client is subscribed (see CrewTrackingPoller).
 */
export const useCrewEta = (timesheetId, jobDate) => {
    const queryClient = useQueryClient();
    const hub = useSignalR('crewTrackingHub');
    const tenantId = useSelector(getTenantId) || 0;
    // Current watch, read by the reconnect handler so it always re-subscribes the active job.
    const watchRef = useRef({ timesheetId: null, tenantId: 0 });
    // @microsoft/signalr has no way to unregister an onreconnected callback, so track which hub
    // instance we've registered on and register at most once per instance — re-running the effect
    // for a new job must not stack another callback.
    const reconnectHubRef = useRef(null);

    const { data } = useQuery({
        queryKey: ['crewEta', timesheetId],
        queryFn: () => httpClient.js(ApiRoutes.jobRequests.crewEta(timesheetId)),
        enabled: Boolean(timesheetId),
        // Positions refresh live over SignalR; the REST snapshot just seeds the initial view.
        staleTime: 120_000,
        retry: false,
    });

    // Subscribe to the open job + apply pushes.
    useEffect(() => {
        if (!hub || !timesheetId) {
            watchRef.current = { timesheetId: null, tenantId: 0 };
            return undefined;
        }

        watchRef.current = { timesheetId, tenantId };

        const onUpdate = (eta) => {
            if (!eta) {
                return;
            }

            queryClient.setQueryData(['crewEta', timesheetId], eta);

            // Move the crew lead truck's marker in the equipment layer (same key MapView uses).
            queryClient.setQueryData(['equipmentForTimesheet', jobDate, timesheetId], (vehicles) => (
                Array.isArray(vehicles)
                    ? vehicles.map((v) => (v.equipmentId === eta.crewEquipmentId
                        ? { ...v, latitude: eta.crewLatitude, longitude: eta.crewLongitude }
                        : v))
                    : vehicles
            ));
        };

        hub.on('CrewLocationUpdated', onUpdate);
        hub.invoke('Subscribe', timesheetId, tenantId)
            .catch((err) => console.error('[crew-eta] Subscribe failed', timesheetId, err));

        // Re-subscribe after an auto-reconnect: the new connection id loses its server-side
        // group + registry entry, so without this the truck/ETA silently freeze. Reads watchRef
        // so it targets the active job and no-ops when nothing is open.
        if (reconnectHubRef.current !== hub) {
            reconnectHubRef.current = hub;
            hub.onreconnected(() => {
                const { timesheetId: tsId, tenantId: watchedTenantId } = watchRef.current;
                if (tsId) {
                    hub.invoke('Subscribe', tsId, watchedTenantId)
                        .catch((err) => console.error('[crew-eta] re-subscribe failed', tsId, err));
                }
            });
        }

        return () => {
            hub.off('CrewLocationUpdated', onUpdate);
            hub.invoke('Unsubscribe', timesheetId).catch(() => {});
            watchRef.current = { timesheetId: null, tenantId: 0 };
        };
    }, [hub, timesheetId, jobDate, queryClient, tenantId]);

    if (!data || !Number.isFinite(data.etaSeconds)) {
        return {
            encodedPolyline: null,
            estimatedArrivalTime: null,
            crewLeadName: null,
        };
    }

    return {
        encodedPolyline: data.encodedPolyline || null,
        // Arrival anchored to the route's compute time; EtaDisplay counts it down live against now.
        estimatedArrivalTime: moment(data.routeComputedAt).add(data.etaSeconds, 'seconds'),
        crewLeadName: data.crewLeadName || null,
    };
};
